import { Button } from "@do/ui/components/button";
import {
  type EmblaCarouselType,
  type EmblaEventListType,
  type EmblaEventModelType,
  type EmblaOptionsType,
} from "embla-carousel"; // TODO: Remove this once we have a stable version of embla-carousel
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getBlockIdentifier,
  type CombinedContainer,
} from "../../../../lib/combine-blocks-and-containers";
import { MessageCard } from "./card";

const TWEEN_FACTOR_BASE = 0.15;
const LARGE_SCREEN_BREAKPOINT_PX = 804;

const getOptions = (isLargeScreen: boolean): EmblaOptionsType => ({
  loop: true,
  slidesToScroll: isLargeScreen ? 2 : 1,
});

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

type MessageCarouselProps = {
  carousel: CombinedContainer;
  messageId: string | undefined;
};

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.goToPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.goToNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canGoToPrev());
    setNextBtnDisabled(!emblaApi.canGoToNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reinit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

export const MessageCarousel = ({ carousel, messageId }: MessageCarouselProps) => {
  const [isLargeScreen, setIsLargeScreen] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= LARGE_SCREEN_BREAKPOINT_PX
  );

  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${LARGE_SCREEN_BREAKPOINT_PX}px)`);
    const handler = () => setIsLargeScreen(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  const slides =
    carousel.blocks.length % 2 !== 0 && isLargeScreen
      ? [...carousel.blocks, ...carousel.blocks]
      : carousel.blocks;
  const options = getOptions(isLargeScreen);

  const [emblaRef, emblaApi] = useEmblaCarousel(options);

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi);

  const tweenFactor = useRef(0);
  const tweenNodes = useRef<(HTMLElement | null)[]>([]);

  const setTweenNodes = useCallback((emblaApi: EmblaCarouselType): void => {
    tweenNodes.current = emblaApi.slideNodes().map((slideNode) => {
      return slideNode.querySelector<HTMLElement>(".embla__slide__number");
    });
  }, []);

  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.snapList().length;
  }, []);

  const tweenScale = useCallback(
    <EventType extends keyof EmblaEventListType>(
      emblaApi: EmblaCarouselType,
      event?: EmblaEventModelType<EventType>
    ) => {
      const engine = emblaApi.internalEngine();
      const scrollProgress = emblaApi.scrollProgress();
      const slidesInView = emblaApi.slidesInView();
      const isScrollEvent = event?.type === "scroll";

      emblaApi.snapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.scrollSnapList.slidesBySnap[snapIndex];

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          const tweenNode = tweenNodes.current[slideIndex];
          if (!tweenNode) return;
          const tweenScaleValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
          const scale = numberWithinRange(tweenScaleValue, 0, 1).toString();
          tweenNode.style.transform = `scale(${scale})`;
          const negativeMargin = `calc(-50% * (1 - ${scale}))`;
          tweenNode.style.marginLeft = negativeMargin;
          tweenNode.style.marginRight = negativeMargin;
        });
      });
    },
    []
  );

  useEffect(() => {
    if (!emblaApi) return;

    setTweenNodes(emblaApi);
    setTweenFactor(emblaApi);
    tweenScale(emblaApi);

    emblaApi
      .on("reinit", setTweenNodes)
      .on("reinit", setTweenFactor)
      .on("reinit", tweenScale)
      .on("scroll", tweenScale)
      .on("slidefocus", tweenScale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, tweenScale]);

  return (
    <div
      key={isLargeScreen ? "lg" : "sm"}
      className="embla relative w-full @5xl/messages:[--carousel-slide-spacing:1.25rem] @2xl/messages:[--carousel-slide-spacing:1rem] @md/messages:[--carousel-slide-spacing:0.75rem] [--carousel-slide-spacing:0.5rem] @3xl/messages:[--carousel-slide-size:35%] @xl/messages:[--carousel-slide-size:55%] @md/messages:[--carousel-slide-size:65%] [--carousel-slide-size:75%]"
    >
      <div className="absolute inset-y-0 left-0 @2xl/messages:w-[50px] w-[35px] h-full bg-gradient-to-l from-transparent to-(--background-color) z-1">
        <div className="relative size-full">
          <Button
            size="icon-xs"
            variant="ghost"
            className="absolute z-2 h-full left-[-20px] @5xl/messages:left-[-30px] [@media(hover:none)_and_(pointer:coarse)]:hidden top-1/2 -translate-y-1/2 hover:bg-transparent hover:opacity-(--carousel-nav-button-hover-opacity)"
            disabled={prevBtnDisabled}
            onClick={onPrevButtonClick}
            aria-label="Previous slide"
            aria-disabled={prevBtnDisabled}
          >
            <ChevronLeftIcon
              className="size-(--carousel-nav-button-size) text-(--carousel-nav-button-color)/(--carousel-nav-button-color-opacity)"
              aria-hidden="true"
            />
          </Button>
        </div>
      </div>
      <div className="absolute inset-y-0 right-0 @2xl/messages:w-[50px] w-[35px] h-full bg-gradient-to-r from-transparent to-(--background-color) z-1">
        <div className="relative size-full">
          <Button
            size="icon-xs"
            variant="ghost"
            className="absolute h-full z-2 right-[-20px] @5xl/messages:right-[-30px] [@media(hover:none)_and_(pointer:coarse)]:hidden top-1/2 -translate-y-1/2 hover:bg-transparent hover:opacity-(--carousel-nav-button-hover-opacity)"
            disabled={nextBtnDisabled}
            onClick={onNextButtonClick}
            aria-label="Next slide"
            aria-disabled={nextBtnDisabled}
          >
            <ChevronRightIcon
              className="size-(--carousel-nav-button-size) text-(--carousel-nav-button-color)/(--carousel-nav-button-color-opacity)"
              aria-hidden="true"
            />
          </Button>
        </div>
      </div>

      <div className="embla__viewport overflow-hidden" ref={emblaRef}>
        <div className="embla__container flex [touch-action:pan-y_pinch-zoom] ml-[calc(var(--carousel-slide-spacing)*-1)]">
          {slides.map((slideBlock, i) => {
            const blockIdentifier = getBlockIdentifier(slideBlock, i);
            return (
              <div
                className="embla__slide flex-[0_0_var(--carousel-slide-size)] min-w-0 pl-[var(--carousel-slide-spacing)]"
                key={i}
              >
                <div className="embla__slide__number">
                  <MessageCard
                    id={`message-${messageId}-carousel-${carousel.id}-slide-${blockIdentifier}-card`}
                    block={slideBlock as CombinedContainer}
                    isInContainer
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
